/**
 * PerformanceTimingSource
 *
 * AudioContext-freie `ITimingSource` für den osmd-extended `PlaybackManager`, Ersatz für die
 * mitgelieferte `LinearTimingSource`. Diese legt pro Instanz einen eigenen `AudioContext` an (nur als
 * Uhr; nie geschlossen → leckt pro Vorschau-Mount einen Context, Chrome deckelt die Anzahl pro Tab),
 * `await`et in `start()` dessen `resume()` und loggt jeden Start/Pause/Reset per `console.log`.
 * Hier: Uhr = `performance.now()` mit expliziter Pause-Akkumulation, keine Nebenwirkungen.
 *
 * Semantik 1:1 wie `AbstractTimingSource`/`LinearTimingSource` im Bundle (verifiziert):
 * - Zeitstempel = `anchorTimestamp + Settings.getDurationAsNoteDuration(vergangene ms)`
 * - `setTimeAndBpm(ts, bpm)` / `setBpm(bpm)` verankern neu und resetten die vergangene Zeit
 * - `pause()` friert die Uhr ein (im Original: `audioContext.suspend()`), `start()` läuft weiter
 * - `start()`/`pause()` liefern Promises – der PlaybackManager `await`et sie.
 *
 * `Settings` (OSMDs nicht exportiertes `PlaybackSettings`) muss vor dem ersten Tick vom Aufrufer
 * gesetzt werden (`osmd.Sheet.SheetPlaybackSetting`), sonst crasht die Playback-Loop im
 * `getDurationInMs` – dieselbe Invariante wie bei der `LinearTimingSource`.
 */
import {Fraction, type ITimingSource} from 'opensheetmusicdisplay';
import type {PlaybackSettings} from 'opensheetmusicdisplay/build/dist/src/Common/DataObjects/PlaybackSettings';

/** Konstante des Originals (dort ungenutzter Alt-Bestand); nur für API-Parität. */
const AUDIO_INPUT_DELAY_MS = 320;

export class PerformanceTimingSource implements ITimingSource {
    Settings!: PlaybackSettings;

    private anchorTimestamp: Fraction = new Fraction();
    /** Vor der aktuellen Laufphase bereits verstrichene Zeit (Summe aller früheren Laufphasen). */
    private accumulatedMs = 0;
    /** Wall-Clock-Zeitpunkt des letzten `start()`; `null` = pausiert. */
    private startedAt: number | null = null;

    constructor(private readonly now: () => number = () => performance.now()) {
    }

    // ---- Uhr ----

    getCurrentTimeInMs(): number {
        return this.accumulatedMs + (this.startedAt === null ? 0 : this.now() - this.startedAt);
    }

    start(): Promise<void> {
        if (this.startedAt === null) this.startedAt = this.now();
        return Promise.resolve();
    }

    pause(): Promise<void> {
        if (this.startedAt !== null) {
            this.accumulatedMs += this.now() - this.startedAt;
            this.startedAt = null;
        }
        return Promise.resolve();
    }

    reset(): void {
        this.accumulatedMs = 0;
        if (this.startedAt !== null) this.startedAt = this.now();
    }

    // ---- Zeitstempel-Arithmetik (AbstractTimingSource) ----

    setBpm(bpm: number): void {
        this.anchorTimestamp = Fraction.createFromFraction(this.getCurrentTimestamp());
        if (this.Settings !== undefined) this.Settings.BeatsPerMinute = bpm;
        this.reset();
    }

    setTimeAndBpm(timestamp: Fraction, bpm?: number): void {
        this.anchorTimestamp = Fraction.createFromFraction(timestamp);
        if (this.Settings !== undefined && bpm) this.Settings.BeatsPerMinute = bpm;
        this.reset();
    }

    getCurrentTimestamp(): Fraction {
        return Fraction.plus(this.getDuration(this.getCurrentTimeInMs()), this.anchorTimestamp);
    }

    getWaitingTimeForTimestampInMs(timestamp: Fraction): number {
        return this.getDurationInMs(Fraction.minus(timestamp, this.getCurrentTimestamp()));
    }

    getDurationInMs(duration: Fraction): number {
        return Math.round(this.Settings.getDurationInMilliseconds(duration));
    }

    getDuration(milliseconds: number): Fraction {
        if (this.Settings === undefined) return new Fraction();
        return this.Settings.getDurationAsNoteDuration(milliseconds);
    }

    getTimestampForTimeInMs(timesInMs: number): Fraction {
        const delta = this.getCurrentTimeInMs() - timesInMs;
        return Fraction.minus(this.getCurrentTimestamp(), this.Settings.getDurationAsNoteDuration(delta));
    }

    getCurrentAudioDelayRevisedTimestamp(): Fraction {
        return Fraction.plus(
            this.getDuration(this.getCurrentTimeInMs() - AUDIO_INPUT_DELAY_MS),
            this.anchorTimestamp,
        );
    }
}
